/**
 * The question box — 03 §8 WP-M4's *"khu hỏi đáp có câu hỏi gợi ý"*.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE ANSWER IS RENDERED BY `InsightCard`, NOT BY A CHAT BUBBLE
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * `AiChatResponse` carries `confidence`, `limitations`, `evidence`, `status` and
 * `fallback_reason` exactly as a stored insight does, and `insightRowFromChat` maps
 * it into that shape so this panel hands the answer to the same component the list
 * uses. A bespoke bubble renderer is precisely where `limitations` would be dropped,
 * because a chat answer "wants" to be one paragraph — and the whole acceptance
 * criterion of this task is that it never is.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  A SUGGESTION FILLS THE BOX AND SENDS NOTHING
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * Two reasons, in `ai/questions.ts`, and the load-bearing one is that a chat turn is
 * the only synchronous model call on this build and holds one of a small number of
 * per-process slots for up to sixty seconds. A suggestion that fired on click would
 * let a stray tap consume half a company's AI concurrency. The test asserts it
 * behaviourally: pressing every suggestion issues **zero** requests.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE ONE DISABLED CONTROL, AND WHY IT IS HONEST
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * The submit is `disabled` while the box is empty or over `maxLength`. That says
 * *"you have not finished writing your question"* — a true statement about the
 * operator's own draft, which is the only kind of greying this build allows
 * (`AccessReasonGate`'s submit, and the two create forms). Nothing here is ever
 * greyed to express a permission or a budget: those are absences, and the budget
 * refusal renders no submit path at all.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 *  THE TRANSCRIPT DIES WITH THE TAB, AND THE PANEL SAYS SO
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * There is no conversation table. `conversation_id` correlates turns in logs and
 * metrics and retrieves nothing; the client holds `history` and re-sends it, and the
 * server re-scopes it on every call. Nothing is written to `localStorage` — for the
 * reason Task 48 gives about the report list: browser memory presented as a company
 * record is the failure mode, and here the "record" would be model prose about
 * named-by-id people.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { AI_CHAT_PATH } from '../../api/paths';
import { useApiMutation } from '../../api/useApiMutation';
import { insightRowFromChat } from '../../ai/insight';
import { readAiRefusal } from '../../ai/refusal';
import { isSendableQuestion, MAX_QUESTION_LENGTH, SUGGESTED_QUESTIONS } from '../../ai/questions';
import { StatusNote } from '../../states/StatusNote';
import { InsightCard } from './InsightCard';
import {
  brainIsUnconfigured,
  brainReplyText,
  isBrainAnswer,
  type AiChatProvider,
  type AiChatTurnRequest,
  type AiChatTurnResponse,
} from './provider';
import { RefusalPanel } from './RefusalPanel';

export interface AskPanelProps {
  readonly onOpenEvidence?: (insightId: string) => void;
  readonly periodStart?: string | undefined;
  readonly periodEnd?: string | undefined;
}

export function AskPanel({ onOpenEvidence, periodStart, periodEnd }: AskPanelProps): ReactNode {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [history, setHistory] = useState<
    readonly { readonly role: 'user' | 'assistant'; readonly content: string }[]
  >([]);
  const [provider, setProvider] = useState<AiChatProvider>('litellm');
  /** Set once the server says this deployment has no Control Plane at all. */
  const [brainOff, setBrainOff] = useState(false);
  /** The question the answer on screen belongs to, so the pair reads as a turn. */
  const [askedShown, setAskedShown] = useState<string | null>(null);
  const mutation = useApiMutation<AiChatTurnRequest, AiChatTurnResponse>(AI_CHAT_PATH);
  /** The question this turn actually sent, so the transcript records what was asked. */
  const asked = useRef<string>('');

  const send = useCallback(
    (question: string) => {
      const trimmed = question.trim();
      if (!isSendableQuestion(trimmed)) return;
      asked.current = trimmed;
      void mutation.submit({
        message: trimmed,
        // Sent only when the operator chose the other door. `undefined` and
        // `'litellm'` take the identical server branch, and a body that always
        // carried one would be this client asserting a routing decision nobody made.
        ...(provider === 'brain' ? { provider } : {}),
        // Omitted on the first turn rather than sent as `null`: the id is
        // **server-minted** and a client that made one up gets a `400`. An absent
        // member is "start a thread"; echoing the server's value continues one.
        ...(conversationId === null ? {} : { conversation_id: conversationId }),
        ...(history.length === 0 ? {} : { history }),
        ...(periodStart === undefined ? {} : { period_start: periodStart }),
        ...(periodEnd === undefined ? {} : { period_end: periodEnd }),
      });
    },
    [mutation, provider, conversationId, history, periodStart, periodEnd],
  );

  const answer = mutation.data;

  // Dependency list hand-checked: there is no `react-hooks` ESLint plugin in this
  // repository, so nothing verifies it (Task 53 §11 item 4, still open). `answer` is
  // the only value read that can change; `asked` is a ref and both setters are stable.
  //
  // The transcript is appended **when an answer arrives**, never at send time: a turn
  // that was refused is not a turn the model saw, and re-sending it as `history`
  // would put the same question on the wire twice — once in `history` and once in
  // `message`.
  //
  // A **brain** turn appends nothing and keeps no id: `conversation_id` on that path
  // is minted per call by this service for correlation and retrieves nothing, and the
  // reply is an opaque body this client must not re-send as a transcript entry it
  // invented the text of.
  useEffect(() => {
    if (answer === null) return;
    setAskedShown(asked.current);
    if (isBrainAnswer(answer)) return;
    setConversationId(answer.conversation_id);
    setHistory((current) => [
      ...current,
      { role: 'user' as const, content: asked.current },
      { role: 'assistant' as const, content: answer.answer },
    ]);
  }, [answer]);

  // The one refusal that is about the option rather than about the turn: there is no
  // BrainAI in this deployment, so the door closes and the question goes back to the
  // door that is open. Every other refusal leaves the choice alone.
  useEffect(() => {
    if (brainIsUnconfigured(mutation.error)) {
      setBrainOff(true);
      setProvider('litellm');
    }
  }, [mutation.error]);

  const usable = isSendableQuestion(draft);
  const brainReply = isBrainAnswer(answer) ? brainReplyText(answer.reply) : null;

  return (
    <section className="wp-section" aria-labelledby="m4-ask-heading" data-testid="m4-ask">
      <div className="wp-ai-head">
        <h2 className="wp-section__title" id="m4-ask-heading">
          {t('m4.ask.title')}
        </h2>
        {/* WP-BR-21. Two doors, one of which this deployment may not have — and when
            it does not, the option carries the reason instead of disappearing, so an
            operator who was told BrainAI exists learns why it is not here. */}
        <div
          className="wp-toggle-group"
          role="group"
          aria-label={t('m4.ask.provider.label')}
          data-testid="m4-ask-provider"
        >
          <button
            type="button"
            className="wp-toggle"
            aria-pressed={provider === 'litellm'}
            data-testid="m4-ask-provider-litellm"
            onClick={() => {
              setProvider('litellm');
            }}
          >
            {t('m4.ask.provider.litellm')}
          </button>
          <button
            type="button"
            className="wp-toggle"
            aria-pressed={provider === 'brain'}
            // `aria-disabled`, not `disabled`: the control keeps its place in the tab
            // order so the tooltip that says WHY is reachable by keyboard. A greyed
            // control nobody can focus is a control that cannot explain itself.
            {...(brainOff ? { 'aria-disabled': true } : {})}
            title={brainOff ? t('m4.ask.provider.brain.off') : t('m4.ask.provider.brain.help')}
            data-testid="m4-ask-provider-brain"
            onClick={() => {
              if (brainOff) return;
              setProvider('brain');
            }}
          >
            {t('m4.ask.provider.brain')}
          </button>
        </div>
      </div>
      <p className="wp-small">{t('m4.ask.body')}</p>
      <p className="wp-small wp-muted" data-testid="m4-ask-provider-note">
        {t(provider === 'brain' ? 'm4.ask.provider.brain.note' : 'm4.ask.provider.litellm.note')}
      </p>
      {brainOff ? (
        <p className="wp-small wp-muted" data-testid="m4-ask-provider-brain-off">
          {t('m4.ask.provider.brain.off')}
        </p>
      ) : null}

      <p className="wp-small wp-muted">{t('m4.ask.suggest.title')}</p>
      <p className="wp-small" data-testid="m4-ask-suggest-note">
        {t('m4.ask.suggest.note')}
      </p>
      {/* `.wp-flags` is the row for inline 12px markers, and it made these read as four
          words of running text rather than four things to press: `--quiet` has no
          border until it is hovered, so at rest there was no target to see (finish
          review, minor e). Their own row, with a hairline on each chip. */}
      <div className="wp-ai-suggest" data-testid="m4-ask-suggestions">
        {SUGGESTED_QUESTIONS.map((suggestion) => (
          <button
            key={suggestion.id}
            type="button"
            className="wp-button wp-button--quiet"
            data-testid={`m4-ask-suggest-${suggestion.id}`}
            onClick={() => {
              // Fills. Sends nothing. See the header.
              setDraft(t(suggestion.questionKey));
            }}
          >
            {t(suggestion.labelKey)}
          </button>
        ))}
      </div>

      <div className="wp-ai-composer">
        <div className="wp-field">
          <label className="wp-field__label" htmlFor="m4-ask-input">
            {t('m4.ask.label')}
          </label>
          <textarea
            className="wp-field__input"
            id="m4-ask-input"
            data-testid="m4-ask-input"
            value={draft}
            rows={3}
            maxLength={MAX_QUESTION_LENGTH}
            onChange={(event) => {
              setDraft(event.target.value);
            }}
          />
          <p className="wp-small wp-muted">{t('m4.ask.rule', { max: MAX_QUESTION_LENGTH })}</p>
        </div>
        <button
          type="button"
          className="wp-button wp-button--primary"
          data-testid="m4-ask-submit"
          disabled={!usable || mutation.status === 'pending'}
          onClick={() => {
            send(draft);
          }}
        >
          {mutation.status === 'pending' ? t('m4.ask.submitting') : t('m4.ask.submit')}
        </button>
      </div>

      <p className="wp-small wp-muted" data-testid="m4-ask-not-persisted">
        {t('m4.ask.notPersisted')}
      </p>
      <p className="wp-small wp-muted" data-testid="m4-ask-no-streaming">
        {t('m4.ask.noStreaming')}
      </p>

      {mutation.status === 'error' && mutation.error !== null ? (
        <RefusalPanel
          refusal={readAiRefusal(mutation.error)}
          error={mutation.error}
          subject={t('m4.subject.answer')}
          {...(readAiRefusal(mutation.error).kind === 'busy'
            ? {
                onRetry: () => {
                  send(draft);
                },
              }
            : {})}
        />
      ) : null}

      {answer === null ? (
        mutation.status === 'error' ? null : (
          <p className="wp-small wp-muted" data-testid="m4-ask-empty">
            {t('m4.ask.empty')}
          </p>
        )
      ) : (
        <div className="wp-ai-thread" data-testid="m4-ask-answer">
          <h3 className="wp-section__title">{t('m4.ask.answer.title')}</h3>
          {/* The question this answer belongs to, printed above it. A page that shows
              only the reply makes the reader trust their memory of what they asked. */}
          {askedShown === null ? null : (
            <p className="wp-ai-turn" data-testid="m4-ask-asked">
              <span className="wp-label">{t('m4.ask.asked')}</span>
              <span>{askedShown}</span>
            </p>
          )}
          {isBrainAnswer(answer) ? (
            // A plain reply block, on purpose. The brain's body carries no
            // confidence, no limitations and no evidence, and pouring it into the
            // card would print four regions the brain never filled.
            <div className="wp-ai-reply" data-testid="m4-ask-brain-answer">
              <p className="wp-label">{t('m4.ask.provider.brain')}</p>
              {brainReply === null ? (
                <pre className="wp-code" data-testid="m4-ask-brain-raw">
                  {JSON.stringify(answer.reply, null, 2)}
                </pre>
              ) : (
                <p data-testid="m4-ask-brain-reply">{brainReply}</p>
              )}
              <p className="wp-small wp-muted" data-testid="m4-ask-brain-run">
                {t('m4.ask.brain.runId')}: <span className="wp-mono">{answer.run_id}</span>
              </p>
              <p className="wp-small wp-muted" data-testid="m4-ask-brain-passthrough">
                {t('m4.ask.brain.passthrough')}
              </p>
            </div>
          ) : (
            <>
              {answer.refused === true ? (
                // The model's own refusal is an **answer**, not an error: it declined
                // because the question asked for a conclusion the data cannot support,
                // or for data outside the caller's scope. The card still renders below
                // it, because the refusal still carries limitations and evidence.
                <StatusNote
                  severity="medium"
                  title={t('m4.ask.refused.title')}
                  testId="m4-ask-refused"
                >
                  <p>{t('m4.ask.refused.body')}</p>
                </StatusNote>
              ) : null}
              <InsightCard
                row={insightRowFromChat(answer as unknown as Readonly<Record<string, unknown>>)}
                headingId="m4-ask-answer-heading"
                {...(onOpenEvidence === undefined ? {} : { onOpenEvidence })}
              />
            </>
          )}
        </div>
      )}
    </section>
  );
}
